import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient, tokenManager} from '@/lib/api';
import { createPkcePair } from '@/lib/pkce';
import { User, SSOAuthCode } from '@/lib/types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { username: string; password: string }) => Promise<boolean>;
  register: (userData: { username: string; email: string; password: string; role: string }) => Promise<boolean>;
  logout: () => void;
  initiateSSOLogin: () => Promise<void>;
  initiateSSORegister: () => Promise<void>;
  handleSSOCallback: (authCode: SSOAuthCode) => Promise<boolean>;
  ssoLogin: (ssoId: string) => Promise<boolean>;
  createOrUpdateSSOUser: (userData: any) => Promise<boolean>;
  checkSSOUserExists: (ssoId: string) => Promise<{ exists: boolean; user?: User }>;
  updateProfile: (userData: Partial<User>) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const SSO_SCOPES = 'openid profile email';

async function redirectToSSOAuthorize(extraParams?: Record<string, string>) {
  const redirectUri = `${window.location.origin}/auth/sso-callback`;
  const state = crypto.randomUUID();
  const nonce = crypto.randomUUID();
  const { verifier, challenge } = await createPkcePair();

  sessionStorage.setItem('sso_state', state);
  sessionStorage.setItem('sso_nonce', nonce);
  sessionStorage.setItem('sso_code_verifier', verifier);

  const ssoServer = import.meta.env.VITE_SSO_APP_URL || 'http://localhost:8001';
  const clientId = import.meta.env.VITE_SSO_CLIENT_ID || '';

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: SSO_SCOPES,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    ...extraParams,
  });

  window.location.href = `${ssoServer}/api/v1/oauth/authorize/?${params.toString()}`;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

    const initializeAuth = async () => {
      try {
        const storedUser = tokenManager.getUser();
        const token = tokenManager.getAccessToken();

        if (storedUser && token) {
          const response = await apiClient.getCurrentUserProfile();
          if (response.success && response.data) {
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          } else {
            tokenManager.clearTokens();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        tokenManager.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };
  useEffect(() => {
    initializeAuth();
  }, []);

  const login = async (credentials: { username: string; password: string }): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiClient.login(credentials);
      
      if (response.success && response.data) {
        tokenManager.setTokens(response.data);
        setUser(response.data.user);
        return true;
      } else {
        console.error('Login failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: { username: string; email: string; password: string; role: string }): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiClient.register(userData);

      if (response.success) {
        return await login({ username: userData.username, password: userData.password });
      } else {
        console.error('Registration failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    tokenManager.clearTokens();
    setUser(null);
    window.location.href = '/auth/login';
  };

  const ssoLogin = async (ssoId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiClient.ssoLogin(ssoId);
      
      if (response.success && response.data) {
        tokenManager.setTokens(response.data);
        setUser(response.data.user);
        return true;
      } else {
        console.error('SSO login failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('SSO login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const createOrUpdateSSOUser = async (userData: any): Promise<boolean> => {
    try {
      setIsLoading(true);
      const storedUser = tokenManager.getUser();
      const newData = {...userData, sso_id: storedUser?.sso_id || ''}
      const response = await apiClient.createOrUpdateSSOUser(newData);
      
      if (response.success && response.data) {
        tokenManager.setTokens(response.data);
        setUser(response.data.user);
        return true;
      } else {
        console.error('SSO user creation failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('SSO user creation error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const checkSSOUserExists = async (ssoId: string): Promise<{ exists: boolean; user?: User }> => {
    try {
      const response = await apiClient.checkSSOUserExists(ssoId);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        console.error('SSO user check failed:', response.error);
        return { exists: false };
      }
    } catch (error) {
      console.error('SSO user check error:', error);
      return { exists: false };
    }
  };

  const updateProfile = async (userData: Partial<User>): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiClient.updateProfile(userData);

      if (response.success && response.data) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        return true;
      } else {
        console.error('Profile update failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const initiateSSOLogin = async () => {
    await redirectToSSOAuthorize();
  };

  const initiateSSORegister = async () => {
    await redirectToSSOAuthorize({ mode: 'register' });
  };

  const handleSSOCallback = async (authCode: SSOAuthCode): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      const storedState = sessionStorage.getItem('sso_state');
      if (authCode.state && storedState && authCode.state !== storedState) {
        console.error('SSO state mismatch');
        return false;
      }

      const codeVerifier = sessionStorage.getItem('sso_code_verifier');
      const nonce = sessionStorage.getItem('sso_nonce');

      sessionStorage.removeItem('sso_state');
      sessionStorage.removeItem('sso_code_verifier');
      sessionStorage.removeItem('sso_nonce');
      
      const response = await apiClient.exchangeAuthCode({
        ...authCode,
        code_verifier: codeVerifier || undefined,
        nonce: nonce || undefined,
      });
      
      if (response.success && response.data) {
        tokenManager.setTokens(response.data);
        setUser(response.data.user);
        return true;
      } else {
        console.error('SSO callback failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('SSO callback error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    initiateSSOLogin,
    initiateSSORegister,
    handleSSOCallback,
    ssoLogin,
    createOrUpdateSSOUser,
    checkSSOUserExists,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
